import React from 'react';
import { useIntl } from 'react-intl';
import msgId from 'resources/intl';
import Plotly, { Layout, LayoutAxis, Shape, PlotMouseEvent, Annotations } from 'plotly.js';
import createPlotlyComponent from 'react-plotly.js/factory';
import colors from 'resources/colors';
import { AnalysisGearAnomalyDetectionValue } from 'models/analysis';
import { Box, styled, SxProps } from '@mui/material';

const Plot = createPlotlyComponent(Plotly);
const axisFontColor = colors.chart.analysis.rexPropellerInvolvement.axisFontColor;
const gridColor = colors.chart.analysis.rexPropellerInvolvement.gridColor;
const graphHeight = 130;

const PlotPanelDiv = styled('div')({
  position: 'relative',
  width: '50%',
  padding: 0,
  float: 'left',
  borderLeft: `1px solid ${colors.chart.legend.border}`,
  borderBottom: `1px solid ${colors.chart.legend.border}`,
});

const HoverDiv = styled('div')({
  position: 'absolute',
  color: 'white',
  background: 'black',
  zIndex: 2,
  fontFamily: 'Arial, sans-serif',
  fontSize: 13,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const plotLStyle: React.CSSProperties = {
  width: '100%',
  height: graphHeight,
  background: colors.chart.legend.background,
};

const plotRStyle: React.CSSProperties = {
  width: '100%',
  height: graphHeight,
  background: colors.chart.legend.background,
};

const TitleDiv = styled('div')({
  height: '100%',
  width: `50%`,
  float: 'left',
  color: 'white',
  fontSize: '16px',
  paddingLeft: '40px',
  paddingTop: '2px',
  borderLeft: `1px solid ${colors.chart.legend.border}`,
});

interface RexGearAnomalyDetectionChartProps {
  sx?: SxProps;
  titleId: string;
  dates: number[];
  ShaftPort: AnalysisGearAnomalyDetectionValue;
  ShaftStarboard: AnalysisGearAnomalyDetectionValue;
}

export function RexGearAnomalyDetectionChart(
  props: RexGearAnomalyDetectionChartProps
): JSX.Element {
  const intl = useIntl();
  const { sx, titleId, dates, ShaftPort, ShaftStarboard } = props;

  const yDataL: number[][] = [[], [], []];
  const yDataR: number[][] = [[], [], []];
  const yTextL: string[] = [];
  const yTextR: string[] = [];
  const plotDataListL: Plotly.Data[] = [];
  const plotDataListR: Plotly.Data[] = [];
  const sharpL = [];
  const sharpR = [];

  const getHoverJudgeText = (risk: number): string => {
    if (risk === 0) {
      return intl.formatMessage({ id: msgId.labelNormal });
    } else if (risk === 1) {
      return '<font color="red">' + intl.formatMessage({ id: msgId.labelRisk }) + '</font>';
    } else {
      return '---';
    }
  };

  const nitiji = intl.formatMessage({ id: msgId.AnalysisHoverInfoLabelDate });
  dates.forEach((date, index) => {
    const lColor = ShaftPort.colors[index];
    yDataL[0][index] = NaN;
    yDataL[1][index] = NaN;
    yDataL[2][index] = NaN;
    if (!isNaN(lColor)) {
      yDataL[lColor][index] = ShaftPort.values[index];
    }
    const rColor = ShaftStarboard.colors[index];
    yDataR[0][index] = NaN;
    yDataR[1][index] = NaN;
    yDataR[2][index] = NaN;
    if (!isNaN(rColor)) {
      yDataR[rColor][index] = ShaftStarboard.values[index];
    }
    const d = new Date(date);
    const dateStrings = d.toISOString().split('T');
    const dateString = dateStrings[0].split('-').join('/');

    yTextL[index] =
      nitiji +
      ':' +
      dateString +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA02 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A02) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA05 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A05) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA08 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A08) +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA09 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A09) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA11 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A11) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA13 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A13) +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA14 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A14) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA16 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A16) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA17 }) +
      '：' +
      getHoverJudgeText(ShaftPort.details[index].A17) +
      '　';

    yTextR[index] =
      nitiji +
      ':' +
      dateString +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA02 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A02) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA05 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A05) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA08 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A08) +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA09 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A09) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA11 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A11) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA13 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A13) +
      '<br>' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA14 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A14) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA16 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A16) +
      '　' +
      intl.formatMessage({ id: msgId.rexAnalysisSensorA17 }) +
      '：' +
      getHoverJudgeText(ShaftStarboard.details[index].A17) +
      '　';
  });
  const timezoneOffset = new Date().getTimezoneOffset() * 1000 * 60;
  yDataL.forEach((data, index) => {
    const gearAnomalyDetectionDataL = {
      x: dates.map((x) => x + timezoneOffset),
      y: data,
      type: 'scatter',
      yaxis: 'y',
      mode: 'markers',
      line: { color: colors.chart.analysis.rexGearAnomalyDetection.incDegColors[index] },
      hoverinfo: 'none',
    } as Plotly.Data;
    plotDataListL.push(gearAnomalyDetectionDataL);
  });
  yDataR.forEach((data, index) => {
    const gearAnomalyDetectionDataR = {
      x: dates.map((x) => x + timezoneOffset),
      y: data,
      type: 'scatter',
      yaxis: 'y',
      mode: 'markers',
      line: { color: colors.chart.analysis.rexGearAnomalyDetection.incDegColors[index] },
      hoverinfo: 'none',
    } as Plotly.Data;
    plotDataListR.push(gearAnomalyDetectionDataR);
  });

  const shape: Partial<Shape> = {
    type: 'line',
    xref: 'paper',
    yref: 'y',
    x0: 0,
    y0: 1,
    x1: 1,
    y1: 1,
    line: {
      color: colors.chart.analysis.rexGearAnomalyDetection.auxiliaryLineColor,
      width: 1,
      dash: 'dot',
    },
  };
  sharpL.push(shape);
  sharpR.push(shape);

  const shapeLeftLine: Partial<Shape> = {
    type: 'line',
    xref: 'paper',
    yref: 'paper',
    x0: 0,
    y0: 0,
    x1: 0,
    y1: 1,
    line: {
      width: 1,
      color: colors.chart.analysis.rexGearAnomalyDetection.y1TickColor,
    },
  };
  sharpL.push(shapeLeftLine);
  sharpR.push(shapeLeftLine);

  const annotationsRisk: Partial<Annotations> = {
    xref: 'paper',
    yref: 'y',
    text: intl.formatMessage({ id: msgId.labelRisk }),
    showarrow: false,
    xanchor: 'left',
    yanchor: 'bottom',
    y: 1,
    x: 0,
    font: {
      size: 13,
      color: colors.chart.analysis.rexGearAnomalyDetection.auxiliaryLineColor,
    },
  };

  const annotationsNormal: Partial<Annotations> = {
    xref: 'paper',
    yref: 'y',
    text: intl.formatMessage({ id: msgId.labelNormal }),
    showarrow: false,
    xanchor: 'left',
    yanchor: 'bottom',
    y: 0,
    x: 0,
    font: {
      size: 13,
      color: colors.chart.analysis.rexGearAnomalyDetection.axisFontColor,
    },
  };

  const day = 1000 * 60 * 60 * 24;
  let dateNum = 0;
  if (dates.length > 0) {
    dateNum = dates[0];
  } else {
    dateNum = Date.now();
  }
  const d = new Date(dateNum);
  const startDate = new Date(d.getFullYear(), d.getMonth(), 1);
  const endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const xaxis = {
    type: 'date',
    gridcolor: gridColor,
    range: [startDate.getTime() - day, endDate.getTime() + day],
    showticklabels: false,
    side: 'top',
    autorange: false,
    zeroline: true,
    tickfont: { color: 'white' },
    fixedrange: true,
  } as Partial<LayoutAxis>;
  const yaxis = {
    title: {
      text: '',
      font: { color: axisFontColor, size: 11 },
    },
    gridcolor: gridColor,
    ticklabelposition: 'inside',
    ticks: 'inside',
    tick0: 0,
    range: [-0.1, 2.1],
    tickvals: [],
    ticktext: [],
    fixedrange: true,
  } as Partial<LayoutAxis>;

  const layoutL = {
    title: {
      text: intl.formatMessage({ id: titleId }),
      color: axisFontColor,
      x: 0.5,
      y: 0.94,
      font: { color: axisFontColor },
    },
    height: graphHeight,
    autosize: true,
    annotations: [annotationsRisk, annotationsNormal],
    xaxis: xaxis,
    yaxis: yaxis,
    paper_bgcolor: colors.chart.plotarea.background,
    plot_bgcolor: colors.chart.plotarea.background,
    margin: {
      t: 25,
      b: 5,
      l: 35,
      r: 30,
      pad: 2,
    },
    showlegend: false,
    shapes: sharpL,
    hovermode: 'closest',
    hoverlabel: {
      bgcolor: 'black',
      bordercolor: 'black',
      font: {
        color: 'white',
      },
      align: 'left',
    },
    barmode: 'stack',
  } as Partial<Layout>;
  const layoutR = Object.assign({}, layoutL);
  Object.assign(layoutR, {
    title: {
      text: intl.formatMessage({ id: titleId }),
      color: axisFontColor,
      x: 0.5,
      y: 0.94,
      font: { color: axisFontColor },
    },
    shapes: sharpR,
    yaxis: yaxis,
  });
  const plotConfig = {
    displayModeBar: false,
    showTips: false,
    doubleClick: false,
  } as Partial<Plotly.Config>;

  const panel = 'RexGearAnomalyDetection';
  const PlotL = 'PlotL' + panel + titleId;
  const PlotR = 'PlotR' + panel + titleId;
  const hoverIdL = PlotL + 'hover';
  const hoverIdR = PlotR + 'hover';

  const setHoverInfo = (
    data: Readonly<PlotMouseEvent>,
    elementId: string,
    hoverText: string,
    index: number
  ) => {
    const div = document.getElementById(elementId);
    const plot = document.getElementById(PlotL);
    if (div && plot) {
      const plotClientWidth = plot.clientWidth;
      div.innerHTML = hoverText;
      div.style.top = '30px';
      if (plotClientWidth > data.event.offsetX + 230) {
        div.style.left = data.event.offsetX + 5 + 'px';
      } else {
        if (data.event.offsetX - 230 < 5) {
          div.style.left = '5px';
        } else {
          div.style.left = data.event.offsetX - 230 + 'px';
        }
      }
      div.style.padding = '3px';
    }
  };

  const handleHoverInfo = (event: Readonly<PlotMouseEvent>, elementId: string) => {
    if (event && event.points && event.points[0]) {
      const index = event.points[0].pointIndex;
      let hoverText = yTextL[index];
      if (elementId !== hoverIdL) {
        hoverText = yTextR[index];
      }
      setHoverInfo(event, elementId, hoverText, index);
    }
  };

  const handleUnhover = (event: Readonly<Plotly.PlotMouseEvent>) => {
    let e = document.getElementById(hoverIdL);
    if (e) {
      e.innerHTML = '';
      e.style.padding = '0px';
    }
    e = document.getElementById(hoverIdR);
    if (e) {
      e.innerHTML = '';
      e.style.padding = '0px';
    }
  };

  return (
    <Box sx={sx}>
      {titleId === msgId.rexGearAnomalyDetectionUpperMain && (
        <React.Fragment>
          <TitleDiv>{intl.formatMessage({ id: msgId.trendLabelPort })}</TitleDiv>
          <TitleDiv>{intl.formatMessage({ id: msgId.trendLabelStbd })}</TitleDiv>
        </React.Fragment>
      )}
      <PlotPanelDiv>
        <HoverDiv id={hoverIdL} />
        <Plot
          divId={PlotL}
          style={plotLStyle}
          data={plotDataListL}
          useResizeHandler
          layout={layoutL}
          config={plotConfig}
          onHover={(event) => handleHoverInfo(event, hoverIdL)}
          onUnhover={handleUnhover}
        />
      </PlotPanelDiv>
      <PlotPanelDiv>
        <HoverDiv id={hoverIdR} />
        <Plot
          divId={PlotR}
          style={plotRStyle}
          data={plotDataListR}
          useResizeHandler
          layout={layoutR}
          config={plotConfig}
          onHover={(event) => handleHoverInfo(event, hoverIdR)}
          onUnhover={handleUnhover}
        />
      </PlotPanelDiv>
    </Box>
  );
}
